import React, { useState } from 'react';
import { API_BASE, getToken } from '../services/apiClient';

// "Report app issue or request" (Pat, Sep 24 2026). Sits where Edit Project was on the
// job banner. A short description, which kind it is, and a screenshot if they have one
// (pick a file or paste one straight in). Everything lands on the Executive page's
// App Issues & Requests tile and the Executives get a bell.
export default function ReportIssueButton({ style, label = '🐞 Report app issue or request' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} style={style} title="Something wrong in the app, or something you'd like it to do? Tell the office.">{label}</button>
      {open && <ReportIssueModal onClose={() => setOpen(false)} />}
    </>
  );
}

const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', color: '#111' };
const btn = { padding: '10px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 14, fontWeight: 700, color: '#374151', cursor: 'pointer' };

export function ReportIssueModal({ onClose, onSent, title = '🐞 Report an app issue or request' }) {
  const [kind, setKind] = useState('issue');
  const [text, setText] = useState('');
  // Several screenshots (Ryan, Oct 5 2026): each { file, preview }.
  const [shots, setShots] = useState([]);
  const MAX_SHOTS = 10;
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  function pick(list) {
    const imgs = [...(list || [])].filter(f => (f.type || '').startsWith('image/'));
    if (!imgs.length) return;
    setShots(cur => [...cur, ...imgs.map(f => {
      let preview = '';
      try { preview = URL.createObjectURL(f); } catch { preview = ''; }
      return { file: f, preview };
    })].slice(0, MAX_SHOTS));
  }
  const removeShot = i => setShots(cur => cur.filter((_, k) => k !== i));
  // A screenshot pasted from the clipboard is the quickest way to hand one over.
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.files || [])].filter(x => (x.type || '').startsWith('image/'));
    if (imgs.length) { e.preventDefault(); pick(imgs); }
  }
  async function send() {
    if (!text.trim()) { setErr('Say in a line what happened, or what you would like.'); return; }
    setSending(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('description', text.trim());
      fd.append('page', window.location.pathname + window.location.search);
      shots.forEach(({ file }, i) => {
        // A pasted image can arrive without a usable name; the upload needs an extension.
        const name = /\.[a-z0-9]+$/i.test(file.name || '') ? file.name : `screenshot-${i + 1}.${(file.type || '').split('/')[1] || 'png'}`;
        fd.append('screenshot', file, name);
      });
      const r = await fetch(`${API_BASE}/app-issues`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not send it. Try again.');
      setDone(true);
      if (onSent) onSent(j);
    } catch (e) { setErr(e.message || 'Could not send it. Try again.'); }
    setSending(false);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 7000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, width: 'min(520px, 96vw)', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{title}</div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {done ? (
          <>
            <div style={{ fontSize: 15, color: '#15803d', fontWeight: 700 }}>✓ Sent. It's on the Executive page now, and they've been pinged.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={onClose} style={{ ...btn, background: '#1e3a5f', color: '#fff', border: 'none' }}>Done</button></div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['issue', '🐞 Something is wrong'], ['request', '💡 I would like something']].map(([k, l]) => (
                <button key={k} onClick={() => setKind(k)}
                  style={{ ...btn, flex: 1, border: `2px solid ${kind === k ? '#1e3a5f' : '#e5e7eb'}`, background: kind === k ? '#eff6ff' : '#fff', color: kind === k ? '#1e3a5f' : '#374151' }}>
                  {l}
                </button>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>A short description</div>
              <textarea value={text} onChange={e => setText(e.target.value)} onPaste={onPaste} rows={4} autoFocus
                placeholder={kind === 'issue' ? 'What happened, and what you expected instead…' : 'What you would like the app to do…'}
                style={{ ...inp, resize: 'vertical' }} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>Screenshots{shots.length ? ` · ${shots.length}` : ''}</div>
              <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>Screenshots help a lot — add as many as you need (up to {MAX_SHOTS}). Pick them here, or paste them into the box above one after another.</div>
              {shots.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                  {shots.map((sh, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      {sh.preview
                        ? <img src={sh.preview} alt={`Screenshot ${i + 1}`} style={{ width: 110, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }} />
                        : <div style={{ width: 110, height: 80, borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#6b7280' }}>Screenshot {i + 1}</div>}
                      <button onClick={() => removeShot(i)} title="Remove this one" aria-label="Remove this screenshot"
                        style={{ position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: 11, border: '2px solid #fff', background: '#b91c1c', color: '#fff', fontSize: 12, fontWeight: 900, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {shots.length < MAX_SHOTS && (
                <label style={{ ...btn, display: 'inline-block', padding: '8px 14px', fontSize: 13, border: '1.5px dashed #9ca3af' }}>
                  📷 {shots.length ? 'Add another screenshot' : 'Add screenshots'}
                  <input type="file" accept="image/*" multiple onChange={e => { pick(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            {err && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 700 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={sending} style={btn}>Cancel</button>
              <button onClick={send} disabled={sending || !text.trim()} style={{ ...btn, background: text.trim() ? '#1e3a5f' : '#e5e7eb', color: text.trim() ? '#fff' : '#9ca3af', border: 'none' }}>
                {sending ? 'Sending…' : 'Send to the office'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
